import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import {
  Job,
  JobRecurrenceRule,
  JobSavePayload,
  JobStatus,
  RecurrenceIntervalUnit,
  Site,
} from '../../types/site';
import {
  formatOccurrenceDateInput,
  getEndJobOccurrenceOptions,
} from '../../lib/jobRecurrence';
import { useProjectStore } from '../../store/projectStore';
import { useUsersStore } from '../../store/usersStore';

interface JobModalProps {
  isOpen: boolean;
  onClose: () => void;
  job?: Job | null;
  recurrenceRule?: JobRecurrenceRule | null;
  onSave: (payload: JobSavePayload, siteId: string | null) => void;
  onEndRecurrence?: (lastOccurrenceDate: Date) => Promise<boolean>;
  sites?: Site[];
  selectedSiteId?: string | null;
}

type JobKind = 'once-off' | 'recurring';

const JobModal: React.FC<JobModalProps> = ({
  isOpen,
  onClose,
  job,
  recurrenceRule,
  onSave,
  onEndRecurrence,
  sites = [],
  selectedSiteId = null,
}) => {
  const { appTheme } = useProjectStore();
  const { users, fetchUsers } = useUsersStore();
  const isDarkTheme = appTheme === 'dark';
  const isEditingRecurring = !!recurrenceRule;
  const isEditingOnceOff = !!job && !recurrenceRule;

  const [jobKind, setJobKind] = useState<JobKind>('once-off');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<JobStatus>('pending');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [intervalCount, setIntervalCount] = useState(1);
  const [intervalUnit, setIntervalUnit] = useState<RecurrenceIntervalUnit>('weeks');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showEndJobPanel, setShowEndJobPanel] = useState(false);
  const [selectedEndOccurrence, setSelectedEndOccurrence] = useState('');
  const [isEndingSeries, setIsEndingSeries] = useState(false);

  const formatDateForInput = (date: Date | undefined): string => {
    if (!date) return '';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayInput = () => formatDateForInput(new Date());

  useEffect(() => {
    if (recurrenceRule) {
      setJobKind('recurring');
      setTitle(recurrenceRule.title);
      setDescription(recurrenceRule.description || '');
      setStatus('pending');
      setPriority(recurrenceRule.priority || 'medium');
      setAssignedTo(recurrenceRule.assignedTo || '');
      setDueDate('');
      setIntervalCount(recurrenceRule.intervalCount);
      setIntervalUnit(recurrenceRule.intervalUnit);
      setStartDate(formatDateForInput(recurrenceRule.startDate));
      setEndDate(formatDateForInput(recurrenceRule.endDate));
      setSelectedSite(recurrenceRule.siteId);
      setShowEndJobPanel(false);
      setSelectedEndOccurrence('');
      return;
    }

    if (job) {
      setJobKind('once-off');
      setTitle(job.title);
      setDescription(job.description || '');
      setStatus(job.status);
      setPriority(job.priority || 'medium');
      setAssignedTo(job.assignedTo || '');
      setDueDate(formatDateForInput(job.dueDate));
      setIntervalCount(1);
      setIntervalUnit('weeks');
      setStartDate(formatDateForInput(job.dueDate) || todayInput());
      setEndDate('');
      if (selectedSiteId) {
        setSelectedSite(selectedSiteId);
      } else if (sites.length > 0) {
        const siteWithJob = sites.find(site => site.jobs?.some(j => j.id === job.id));
        if (siteWithJob) setSelectedSite(siteWithJob.id);
      }
      return;
    }

    setJobKind('once-off');
    setTitle('');
    setDescription('');
    setStatus('pending');
    setPriority('medium');
    setAssignedTo('');
    setDueDate('');
    setSelectedSite(selectedSiteId || '');
    setIntervalCount(1);
    setIntervalUnit('weeks');
    setStartDate(todayInput());
    setEndDate('');
    setShowEndJobPanel(false);
    setSelectedEndOccurrence('');
  }, [job, recurrenceRule, isOpen, selectedSiteId, sites]);

  useEffect(() => {
    if (isOpen) fetchUsers();
  }, [isOpen, fetchUsers]);

  const assigneeOptions = React.useMemo(() => {
    const names = users.map(u => u.displayName).filter(Boolean);
    if (assignedTo && !names.includes(assignedTo)) {
      names.unshift(assignedTo);
    }
    return names;
  }, [users, assignedTo]);

  const endJobOccurrences = React.useMemo(() => {
    if (!recurrenceRule) return [];
    const siteJobs = sites.find((s) => s.id === recurrenceRule.siteId)?.jobs;
    return getEndJobOccurrenceOptions(recurrenceRule, siteJobs, recurrenceRule.id);
  }, [recurrenceRule, sites]);

  useEffect(() => {
    if (!showEndJobPanel || !recurrenceRule) return;
    if (selectedEndOccurrence) return;
    const preset = recurrenceRule.endDate
      ? formatOccurrenceDateInput(recurrenceRule.endDate)
      : endJobOccurrences.length > 0
        ? formatOccurrenceDateInput(endJobOccurrences[endJobOccurrences.length - 1])
        : '';
    if (preset) setSelectedEndOccurrence(preset);
  }, [showEndJobPanel, recurrenceRule, endJobOccurrences, selectedEndOccurrence]);

  const handleConfirmEndJob = async () => {
    if (!onEndRecurrence || !selectedEndOccurrence) return;
    setIsEndingSeries(true);
    try {
      const ok = await onEndRecurrence(new Date(`${selectedEndOccurrence}T00:00:00`));
      if (ok) {
        setShowEndJobPanel(false);
        onClose();
      }
    } finally {
      setIsEndingSeries(false);
    }
  };

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    const siteIdToUse = recurrenceRule
      ? recurrenceRule.siteId
      : job && !selectedSite
        ? (selectedSiteId || sites.find(s => s.jobs?.some(j => j.id === job.id))?.id || null)
        : (selectedSite || null);

    const isRecurring = jobKind === 'recurring' || isEditingRecurring;

    if (isRecurring && !siteIdToUse) {
      alert('Please select a site for recurring jobs.');
      return;
    }

    if (isRecurring) {
      if (!startDate) {
        alert('Please choose a start date for the recurring schedule.');
        return;
      }
      if (intervalCount < 1) {
        alert('Repeat interval must be at least 1.');
        return;
      }

      onSave({
        job: {
          title: title.trim(),
          description: description.trim() || undefined,
          status: 'pending',
          priority,
          assignedTo: assignedTo.trim() || undefined,
        },
        recurrence: {
          intervalCount,
          intervalUnit,
          startDate: new Date(startDate),
          endDate: endDate ? new Date(endDate) : undefined,
        },
      }, siteIdToUse);
      onClose();
      return;
    }

    onSave({
      job: {
        title: title.trim(),
        description: description.trim() || undefined,
        status,
        priority,
        assignedTo: assignedTo.trim() || undefined,
        dueDate: dueDate ? new Date(dueDate) : undefined,
      },
    }, siteIdToUse);

    onClose();
  };

  const inputClass = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
    isDarkTheme
      ? 'bg-[#252a32] border-[#434a55] text-white focus:ring-gray-500'
      : 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500'
  } focus:border-transparent text-sm`;

  const segmentBtn = (active: boolean) =>
    `flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
      active
        ? (isDarkTheme ? 'bg-gray-600 text-white' : 'bg-blue-600 text-white')
        : (isDarkTheme ? 'bg-[#252a32] text-gray-300 hover:bg-[#323944]' : 'bg-gray-700 text-gray-300 hover:bg-gray-600')
    }`;

  const showRecurringFields = jobKind === 'recurring' || isEditingRecurring;
  const showOnceOffFields = !showRecurringFields;
  const canToggleKind = !job && !recurrenceRule;
  const siteRequired = showRecurringFields;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className={`rounded-lg shadow-2xl w-full max-w-md max-h-[min(90vh,100%)] flex flex-col overflow-hidden border ${
        isDarkTheme
          ? 'bg-[#1b2027] border-[#343a43]'
          : 'bg-gray-800 border-gray-700'
      }`}>
        <div className={`flex-shrink-0 flex items-center justify-between px-6 py-4 border-b ${isDarkTheme ? 'border-[#343a43]' : 'border-gray-700'}`}>
          <h2 className="text-xl font-semibold text-white">
            {recurrenceRule ? 'Edit Recurring Job' : job ? 'Edit Job' : 'Create New Job'}
          </h2>
          <button
            onClick={onClose}
            className={`p-1 rounded transition-colors ${isDarkTheme ? 'hover:bg-[#323944]' : 'hover:bg-gray-700'}`}
            title="Close"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-y-auto modal-scroll">
            <div className="px-6 py-4 space-y-4">
              {canToggleKind && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Job Type</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className={segmentBtn(jobKind === 'once-off')}
                      onClick={() => setJobKind('once-off')}
                    >
                      Once-Off
                    </button>
                    <button
                      type="button"
                      className={segmentBtn(jobKind === 'recurring')}
                      onClick={() => setJobKind('recurring')}
                    >
                      Recurring
                    </button>
                  </div>
                </div>
              )}

              {(showOnceOffFields || showRecurringFields) && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">
                    Site{siteRequired ? <span className="text-red-500"> *</span> : null}
                  </label>
                  <select
                    value={selectedSite}
                    onChange={(e) => setSelectedSite(e.target.value)}
                    className={inputClass}
                    required={siteRequired}
                    disabled={!!job || !!recurrenceRule}
                  >
                    {!siteRequired && <option value="">No site</option>}
                    {sites.map(site => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Job Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={`${inputClass} placeholder-gray-400`}
                  placeholder="e.g., Install new equipment"
                  required
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  className={`${inputClass} placeholder-gray-400 resize-none`}
                  placeholder="Job description..."
                />
              </div>

              {showOnceOffFields && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Status</label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as JobStatus)}
                      className={inputClass}
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Priority</label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                      className={inputClass}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
              )}

              {showRecurringFields && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Priority</label>
                  <select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                    className={inputClass}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Assigned To</label>
                <select
                  value={assignedTo}
                  onChange={(e) => setAssignedTo(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Unassigned</option>
                  {assigneeOptions.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {showOnceOffFields && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Due Date</label>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className={inputClass}
                  />
                </div>
              )}

              {showRecurringFields && (
                <div className="space-y-4 rounded-md border border-[#434a55] p-3">
                  <p className="text-sm font-medium text-gray-200">Repeat schedule</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">Every</label>
                      <input
                        type="number"
                        min={1}
                        value={intervalCount}
                        onChange={(e) => setIntervalCount(Math.max(1, parseInt(e.target.value) || 1))}
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">Unit</label>
                      <select
                        value={intervalUnit}
                        onChange={(e) => setIntervalUnit(e.target.value as RecurrenceIntervalUnit)}
                        className={inputClass}
                      >
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1.5">Start date</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className={inputClass}
                      required
                    />
                  </div>
                  {!isEditingRecurring && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1.5">End date (optional)</label>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className={inputClass}
                      />
                    </div>
                  )}
                  {isEditingRecurring && recurrenceRule?.endDate && (
                    <p className="text-sm text-amber-300/90">
                      Series ends after {formatDateForInput(recurrenceRule.endDate)}.
                      Use End Job below to change or remove the end date.
                    </p>
                  )}
                  <p className="text-xs text-gray-400">
                    A new pending job will be created automatically on each scheduled date.
                  </p>
                </div>
              )}
            </div>
          </div>

          {showEndJobPanel && isEditingRecurring && (
            <div className={`flex-shrink-0 px-6 py-4 border-t space-y-3 ${isDarkTheme ? 'border-[#343a43] bg-[#252a32]/60' : 'border-gray-700 bg-gray-900/40'}`}>
              <p className="text-sm text-gray-200 font-medium">End recurring schedule</p>
              <p className="text-sm text-gray-400">
                Choose the last occurrence date. No new jobs will be created after this date and the recurring schedule will be removed. Existing job instances stay on your list until completed.
              </p>
              {endJobOccurrences.length === 0 ? (
                <p className="text-sm text-amber-300">No scheduled occurrences available to end on.</p>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Last occurrence</label>
                  <select
                    value={selectedEndOccurrence}
                    onChange={(e) => setSelectedEndOccurrence(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Select a date</option>
                    {endJobOccurrences.map((d) => {
                      const value = formatOccurrenceDateInput(d);
                      return (
                        <option key={value} value={value}>
                          {d.toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowEndJobPanel(false)}
                  disabled={isEndingSeries}
                  className={`flex-1 px-3 py-2 border text-gray-300 rounded-md transition-colors text-sm font-medium ${
                    isDarkTheme
                      ? 'border-[#434a55] hover:bg-[#323944]'
                      : 'border-gray-600 hover:bg-gray-700'
                  }`}
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={() => void handleConfirmEndJob()}
                  disabled={!selectedEndOccurrence || isEndingSeries || endJobOccurrences.length === 0}
                  className="flex-1 px-3 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isEndingSeries ? 'Ending…' : 'Confirm End Job'}
                </button>
              </div>
            </div>
          )}

          <div className={`flex-shrink-0 flex space-x-3 px-6 py-4 border-t ${isDarkTheme ? 'border-[#343a43]' : 'border-gray-700'}`}>
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 border text-gray-300 rounded-md transition-colors text-sm font-medium ${
                isDarkTheme
                  ? 'border-[#434a55] hover:bg-[#323944]'
                  : 'border-gray-600 hover:bg-gray-700'
              }`}
            >
              Cancel
            </button>
            {isEditingOnceOff && job.status !== 'cancelled' && (
              <button
                type="button"
                onClick={() => {
                  const siteIdToUse = selectedSite || (selectedSiteId || sites.find(s => s.jobs?.some((j: Job) => j.id === job.id))?.id || '');
                  onSave({
                    job: {
                      title: job.title,
                      description: job.description,
                      status: 'cancelled',
                      priority: job.priority || 'medium',
                      assignedTo: job.assignedTo,
                      dueDate: job.dueDate,
                    },
                  }, siteIdToUse);
                  onClose();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Cancel Job
              </button>
            )}
            {isEditingRecurring && onEndRecurrence && !showEndJobPanel && (
              <button
                type="button"
                onClick={() => setShowEndJobPanel(true)}
                className="px-4 py-2 bg-red-600/90 text-white rounded-md hover:bg-red-700 transition-colors text-sm font-medium"
              >
                End Job
              </button>
            )}
            <button
              type="submit"
              className={`flex-1 px-4 py-2 text-white rounded-md transition-colors text-sm font-medium ${
                isDarkTheme ? 'bg-gray-600 hover:bg-gray-500' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {recurrenceRule ? 'Update Recurring Job' : job ? 'Update Job' : showRecurringFields ? 'Create Recurring Job' : 'Create Job'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobModal;
